<template>
  <div
    :id="props.id"
    ref="notificationWrapper"
    :class="easyNotificationsBem('notification', props.type)"
    data-cy="wrapper__easy-notification"
    @mouseenter="stopTimer"
    @mouseleave="startTimer"
  >
    <EasyAlert
      :closeable="true"
      :variant="VuetifyAlertVariants.Tonal"
      :type="props.type"
      :density="VuetifyDensity.Compact"
      @close="closeNotification"
    >
      <template #prepend>
        <DSIcon
          :class="iconClasses[props.type as EasyAlerts]"
          :icon="iconsMap[props.type as EasyAlerts]"
          :size="DS_ICON_SIZES.Large"
          data-cy="icon__easy-notification"
        />
      </template>

      <template
        v-if="props.title"
        #title
      >
        <span
          ref="titleRef"
          v-easy-tooltip="{ text: props.title, disabled: !isTitleTooltipVisible }"
          data-cy="title__easy-notification"
          class="easy-notifications__notification--title"
        >
          {{ props.title }}
        </span>
      </template>

      <template
        v-if="props.message"
        #text
      >
        <div
          v-dompurify-html="props.message"
          :class="{ 'mt-2': !!props.title }"
          data-cy="message__easy-notification"
          class="easy-notifications__notification--message"
        />
      </template>
    </EasyAlert>

    <div class="flex items-center justify-center">
      <EasyProgressBar
        v-if="!!props.duration"
        :color="props.type as unknown as VuetifyColors"
        data-cy="progress-bar__easy-notification"
        :ratio="timeout"
        rounded="0"
        height="3"
        :bg-opacity="0.15"
        :class="easyNotificationsBem('notification', 'progress')"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import EasyAlert from "@/src/easy-ui/components/EasyAlert.vue";
import { DSIcon } from "@/src/design_system";
import { DS_ICON_SIZES } from "@/src/design_system/primitives/icon/types";
import { DSIconName } from "@/src/design_system/primitives/icon/types/iconName.generated";
import EasyProgressBar from "@/src/easy-ui/components/EasyProgressBar.vue";
import easyNotificationsStore from "@/src/easy-ui/store/easyNotificationsStore";
import isNull from "lodash/isNull";
import { DEFAULT_NOTIFICATION_DURATION, PROGRESS_BAR_CHUNKS } from "@/src/easy-ui/constants/easyNotifications";
import { EasyAlerts, VuetifyAlertVariants, VuetifyColors, VuetifyDensity } from "@/src/easy-ui/types";
import { EasyNotificationProps } from "@/src/easy-ui/types/easyNotifications";
import { easyNotificationsBem } from "@/src/easy-ui/utils/easyNotifications";
import { onMounted, ref } from "vue";
import { useTooltipVisibility } from "@/src/shared/composables/tooltipVisibility";

const iconsMap: Record<EasyAlerts, DSIconName> = {
  [EasyAlerts.Error]: "o-warning",
  [EasyAlerts.Success]: "c-check",
  [EasyAlerts.Warning]: "warning-sign",
  [EasyAlerts.Info]: "c-info",
};

const iconClasses = {
  [EasyAlerts.Error]: "text-negative",
  [EasyAlerts.Success]: "text-positive",
  [EasyAlerts.Warning]: "text-warning",
  [EasyAlerts.Info]: "text-text",
};

const props = withDefaults(defineProps<EasyNotificationProps>(), {
  duration: DEFAULT_NOTIFICATION_DURATION,
  id: undefined,
  message: undefined,
  title: undefined,
  type: EasyAlerts.Error,
});

const notificationWrapper = ref<HTMLDivElement>();

const closeNotification = () => {
  clearInterval(durationInterval.value);
  easyNotificationsStore.removeNotification(props.id);
};

const durationInterval = ref<number>(0);

const timeout = ref<number>(0);

const titleRef = ref<HTMLElement | null>(null);

const { isElementVisible: isTitleTooltipVisible } = useTooltipVisibility(titleRef);

const stopTimer = () => clearInterval(durationInterval.value);

const startTimer = () => {
  if (isNull(props.duration)) return;
  durationInterval.value = window.setInterval(() => {
    if (timeout.value > PROGRESS_BAR_CHUNKS) closeNotification();
    ++timeout.value;
  }, props.duration / PROGRESS_BAR_CHUNKS);
};

onMounted(() => {
  startTimer();
});
</script>

<style lang="scss">
@use "../../../shared/stylesheets/easy/utils/helpers/functions/index" as *;
@use "../../../shared/stylesheets/easy/variables/index" as *;
@use "../../../shared/stylesheets/easy/utils/abstracts/typography/_typography.mixins.scss" as *;

.easy-notifications__notification {
  position: relative;
  width: fit-content;
  max-width: px(600);
  background-color: var(--easy__color--front);
  border: px(1) solid;
  border-style: solid;
  border-radius: px(12);

  &--title {
    color: var(--easy__color--text);
    font-size: px(20);
    line-height: px(28);

    @include line-clamp(1);
  }

  &--message {
    overflow-y: auto;
    max-height: px(160);
    padding-right: px(8);
    padding-bottom: px(8);
    color: var(--easy__color--text);
    font-size: px(16);
    line-height: px(24);
  }

  &--progress {
    position: absolute;
    bottom: 0;
    border: 0;

    width: calc(100% - px(12));
  }

  &--error {
    border-color: var(--easy__color--negative);

    .easy-notifications__notification--progress {
      border-bottom-color: var(--easy__color--negative);
    }
  }

  &--warning {
    border-color: var(--easy__color--important);

    .easy-notifications__notification--progress {
      border-bottom-color: var(--easy__color--important);
    }
  }

  &--success {
    border-color: var(--easy__color--positive);

    .easy-notifications__notification--progress {
      border-bottom-color: var(--easy__color--positive);
    }
  }

  &--info {
    border-color: var(--easy__notice-border);

    .easy-notifications__notification--progress {
      border-bottom-color: var(--easy__notice-border);
    }
  }
}
</style>
